import type { LanguageCode } from '@repo/domain';
import { useUser, useUserActions } from '@repo/domain-service';
import { changeLanguage } from '@repo/locale';

import { useLanguageStore } from '@shared/stores/useLanguageStore';

/**
 * Single switch point for changing the app language.
 *
 * - `applyFrontLanguage` changes only the front (i18n + persisted browser
 *   store). Use it when the backend save is handled elsewhere, e.g. folded into
 *   a form's own updateUser call.
 * - `switchLanguage` does the full switch: front change plus, when a user is
 *   logged in, persisting the choice to the backend.
 *
 * Every place that changes language should go through this hook so the
 * front/backend logic lives in one spot.
 */
export default function useSwitchLanguage() {
  const { user } = useUser();
  const { updateUser } = useUserActions();
  const setLanguage = useLanguageStore((state) => state.setLanguage);

  const applyFrontLanguage = async (language: LanguageCode): Promise<void> => {
    await changeLanguage(language);
    setLanguage(language);
  };

  const switchLanguage = async (language: LanguageCode): Promise<void> => {
    await applyFrontLanguage(language);

    if (user) {
      await updateUser({
        id: user.id,
        name: user.name,
        phone: user.phone,
        language,
      });
    }
  };

  return { applyFrontLanguage, switchLanguage };
}
